import { Button } from "@/components/ui/button";
import { Trash } from "lucide-react";
import type { RaRecord } from "ra-core";
import { Translate, useDelete, useListContext, useNotify, useRefresh, useUnselectAll } from "ra-core";
import { cn } from "@/lib/utils";
import type { ReactNode } from "react";
import { useCallback } from "react";

export const BulkDeleteButton = ({
  icon = defaultIcon,
  label,
  className,
  resource,
}: BulkDeleteButtonProps) => {
  const { selectedIds } = useListContext();
  const [deleteOne, { isPending }] = useDelete();
  const notify = useNotify();
  const refresh = useRefresh();
  const unselectAll = useUnselectAll(resource);

  const handleDelete = useCallback(async () => {
    if (!selectedIds || selectedIds.length === 0) return;

    try {
      await Promise.all(
        selectedIds.map((id) =>
          deleteOne(
            resource,
            { id, previousData: {} as RaRecord },
            {
              onError: (error: any) => {
                notify(error?.message || 'ra.notification.error', { type: 'error' });
              },
            }
          )
        )
      );
      notify('ra.notification.deleted', {
        type: 'info',
        messageArgs: { smart_count: selectedIds.length },
      });
      unselectAll();
      refresh();
    } catch (error) {
      // Errors are handled in individual delete calls
    }
  }, [selectedIds, deleteOne, resource, notify, unselectAll, refresh]);

  return (
    <Button
      variant="destructive"
      type="button"
      onClick={handleDelete}
      disabled={isPending || !selectedIds || selectedIds.length === 0}
      className={cn("h-9", className)}
    >
      {icon}
      <Translate i18nKey={label ?? "ra.action.delete"}>
        {label ?? "Delete"}
      </Translate>
    </Button>
  );
};

export type BulkDeleteButtonProps = {
  label?: string;
  icon?: ReactNode;
  resource: string;
  className?: string;
};

const defaultIcon = <Trash />;
